import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { GlobalOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import ImageCol from '@components/common/table/ImageCol';
import {
    AppConstants,
    DATE_FORMAT_DISPLAY,
    DATE_FORMAT_VALUE,
    DEFAULT_TABLE_ITEM_SIZE_LARGE,
    EMPTY_DETAIL,
    ExpiredTypes,
} from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { expiredTypes, statusOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime } from '@utils';
import { Button, Flex } from 'antd';
import moment from 'moment';

import RestaurantExpiredForm from './RestaurantExpiredForm';

const message = defineMessages({
    objectName: 'expired-restaurants',
});

const FORM_ID = 'form-restaurant-expired';

const RestaurantExpiredListPage = () => {
    const translate = useTranslate();
    const statusValues = translate.formatKeys(statusOptions, ['label']);

    const { loading: updateRestaurantPinLoading } = useFetch(apiConfig.restaurant.update);

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        loadingSave,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
        isChanged,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.restaurant.getList,
            getById: apiConfig.restaurant.getById,
            create: apiConfig.restaurant.create,
            update: apiConfig.restaurant.update,
            delete: apiConfig.restaurant.delete,
            changeStatus: apiConfig.restaurant.update,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.prepareGetListPathParams = () => {
                return {
                    expiredDateTo: queryFilter.expiredDateTo,
                };
            };
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };

            funcs.additionalActionColumnButtons = () => ({
                webPortal: ({ restaurantData }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.registration)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${AppConstants.webPortal}${restaurantData?.id}`, '_blank');
                            }}
                        >
                            <GlobalOutlined />
                        </Button>
                    </BaseTooltip>
                ),
            });

            funcs.getList = () => {
                const params = {
                    ...queryFilter,
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    expiredDateTo: `${moment().format(DATE_FORMAT_VALUE)} 23:59:59`,
                };
                if (queryFilter.expiredType) {
                    if (queryFilter.expiredType === ExpiredTypes.EXPIRED) {
                        params.expiredDateTo = `${moment().format(DATE_FORMAT_VALUE)} 23:59:59`;
                    } else if (queryFilter.expiredType === ExpiredTypes.NEXT_MONTH_1) {
                        params.expiredDateFrom = `${moment().format(DATE_FORMAT_VALUE)} 00:00:00`;
                        params.expiredDateTo = `${moment().add(1, 'M').format(DATE_FORMAT_VALUE)} 23:59:59`;
                    } else if (queryFilter.expiredType === ExpiredTypes.NEXT_MONTH_2) {
                        params.expiredDateFrom = `${moment().add(1, 'M').format(DATE_FORMAT_VALUE)} 00:00:00`;
                        params.expiredDateTo = `${moment().add(2, 'M').format(DATE_FORMAT_VALUE)} 23:59:59`;
                    } else if (queryFilter.expiredType === ExpiredTypes.NEXT_MONTH_3) {
                        params.expiredDateFrom = `${moment().add(2, 'M').format(DATE_FORMAT_VALUE)} 00:00:00`;
                        params.expiredDateTo = `${moment().add(3, 'M').format(DATE_FORMAT_VALUE)} 23:59:59`;
                    } else if (queryFilter.expiredType === ExpiredTypes.NEXT_MONTH_4) {
                        params.expiredDateFrom = `${moment().add(3, 'M').format(DATE_FORMAT_VALUE)} 00:00:00`;
                        params.expiredDateTo = `${moment().add(4, 'M').format(DATE_FORMAT_VALUE)} 23:59:59`;
                    }
                }

                return mixinFuncs.handleFetchList(params);
            };
        },
    });

    const handleSubmitForm = (values) => {
        const preparedData = isEditing
            ? {
                ...values,
                id: detail?.id,
                status: values.status || detail?.status,
                settings: typeof values.settings === 'string' ? values.settings : JSON.stringify(values.settings),
            }
            : { ...values };
        onSave(preparedData);
    };

    const columns = [
        {
            title: <FormattedMessage defaultMessage="Logo" />,
            dataIndex: 'logoPath',
            align: 'left',
            width: 120,
            render: (logoPath) => (
                <ImageCol path={logoPath ? `${AppConstants.contentRootUrl}${logoPath}` : null} className="img-logo" />
            ),
        },
        { title: <FormattedMessage defaultMessage="Restaurant Name" />, dataIndex: 'restaurantName', width: 150 },
        {
            title: <FormattedMessage defaultMessage="Address" />,
            width: 280,
            dataIndex: ['address'],
        },
        {
            title: <FormattedMessage defaultMessage="ZIP code" />,
            width: 100,
            dataIndex: ['zipCode'],
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 120,
            dataIndex: 'expiredDate',
            render: (expiredDate) => {
                const expiredDateLocal = convertUtcToLocalTime(expiredDate, DATE_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY);
                return <div>{expiredDateLocal}</div>;
            },
        },
        {
            title: <FormattedMessage defaultMessage="Extend Date" />,
            width: 120,
            dataIndex: 'extendDate',
            render: (extendDate) => {
                const extendDateLocal = convertUtcToLocalTime(extendDate, DATE_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY);
                return <div>{extendDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'restaurantName',
            placeholder: translate.formatMessage(commonMessage.restaurantName),
            submitOnChanged: true,
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.status),
            type: FieldTypes.SELECT,
            options: statusValues,
            submitOnChanged: true,
        },
        {
            key: 'expiredType',
            placeholder: translate.formatMessage(commonMessage.expired),
            type: FieldTypes.SELECT,
            options: expiredTypes,
            submitOnChanged: true,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.restaurantsexpired) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading || updateRestaurantPinLoading}
                        pagination={pagination}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <RestaurantExpiredForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY_DETAIL}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={handleSubmitForm}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default RestaurantExpiredListPage;
